import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Shield, CreditCard, Calendar, Check, Globe, Clock, Zap, Server, ShieldCheck, Lock, Database, Cpu, IndianRupee } from 'lucide-react';
import { m } from 'framer-motion';

const HostingerExpiryModal = () => {
    const [isOpen, setIsOpen] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState('premium');
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [isProcessing, setIsProcessing] = useState(false);
    const [timeLeft, setTimeLeft] = useState('');
    const [showCloseButton, setShowCloseButton] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(120); // 5 minutes in seconds

    const expiryDate = "20 May 2026";

    // Prices in Indian Rupees
    const domainRenewalPrice = 1316.44; // For both domains
    const hostingRenewalPrice = 3214.20; // For hosting renewal

    const domains = [
        {
            name: "kanhaiyalogistic.com",
            type: "SSC Exam Portal",
            status: "Active",
            visitors: "1.2K/month",
            renewal: "₹1316.44/yr",
            webrenewal: "₹3214.20/yr",
            serverrenewal: "₹9891.00/yr",
            total: "₹9891.00/yr"
        },
        // {
        //     name: "typingspeedsoftware.com",
        //     type: "Software Platform",
        //     status: "Active",
        //     visitors: "3.4K/month",
        //     renewal: "₹1316.44/yr",
        //     webrenewal: "₹3214.20/yr",
        //     total: "₹4530.64/yr"
        // }
    ];

    // Calculate time until expiration
    useEffect(() => {
        const calculateTimeLeft = () => {
            const expiry = new Date('January 20, 2024 23:59:59');
            const now = new Date();
            const difference = expiry.getTime() - now.getTime();

            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                setTimeLeft(`${days}d ${hours}h ${minutes}m`);
            } else {
                setTimeLeft("EXPIRED");
            }
        };

        // Countdown timer for 5 minutes
        const countdownInterval = setInterval(() => {
            setSecondsLeft(prevSeconds => {
                if (prevSeconds <= 1) {
                    setShowCloseButton(true);
                    clearInterval(countdownInterval);
                    return 0;
                }
                return prevSeconds - 1;
            });
        }, 1000);

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 60000);

        return () => {
            clearInterval(timer);
            clearInterval(countdownInterval);
        };
    }, []);

    const handlePayment = () => {
        setIsProcessing(true);
        // Simulate payment processing
        setTimeout(() => {
            setIsProcessing(false);
            alert(`🎉 Payment Successful! 
      ₹${(hostingRenewalPrice + domainRenewalPrice).toLocaleString('en-IN')} paid successfully.
      Your services are renewed until 20 Jan 2025.`);
            setIsOpen(false);
        }, 2000);
    };

    const handleClose = () => {
        if (window.confirm(`⚠️ Your services will expire in ${timeLeft}. 
    Are you sure you want to close without renewing?`)) {
            setIsOpen(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Dark overlay */}
            <div className="fixed inset-0 bg-black/70 z-[9998] animate-fadeIn"></div>

            {/* Modal Container - Centered and Responsive */}
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fadeIn">
                <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-slideUp">

                    {/* Hostinger Header */}
                    <div className="sticky top-0 bg-gradient-to-r from-[#673AB7] via-[#7E57C2] to-[#673AB7] p-4 sm:p-6 rounded-t-2xl z-10">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center space-x-3 sm:space-x-4">
                                <div className="flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 bg-white/20 rounded-xl backdrop-blur-sm animate-pulse">
                                    <div className="text-white font-bold text-xl sm:text-3xl">H</div>
                                </div>
                                <div>
                                    <h1 className="text-xl sm:text-3xl font-bold text-white tracking-tight">Hostinger India</h1>
                                    <p className="text-white/90 text-sm sm:text-lg">⚠️ IMPORTANT: Your Services is Expired. kindly Pay Bill then your sevices will resume.</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3 self-end sm:self-center">
                                {/* <div className="text-center px-3 py-1 sm:px-4 sm:py-2 bg-red-500/30 backdrop-blur-sm rounded-xl border border-red-500/50">
                                    <div className="text-xs text-red-200">Expires in</div>
                                    <div className="text-base sm:text-xl font-bold text-white animate-pulse">{timeLeft}</div>
                                </div> */}
                                {showCloseButton ? (
                                    <button
                                        onClick={handleClose}
                                        className="p-2 sm:p-3 hover:bg-white/10 rounded-full transition-all hover:rotate-90 duration-300"
                                    >
                                        <X className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                                    </button>
                                ) : (
                                    <div className="text-center px-3 py-1 sm:px-4 sm:py-2 bg-blue-500/30 backdrop-blur-sm rounded-xl border border-blue-500/50">
                                        <div className="text-xs text-blue-200">Please wait</div>
                                        <div className="text-base sm:text-xl font-bold text-white">
                                            {Math.floor(secondsLeft / 60)}:{(secondsLeft % 60).toString().padStart(2, '0')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 sm:p-6 md:p-8 rounded-b-2xl border border-purple-500/30">
                        {/* Urgent Alert Banner */}
                        <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 border border-red-500/40 rounded-2xl animate-pulseBorder">
                            <div className="flex flex-col sm:flex-row items-start gap-4">
                                <div className="flex-shrink-0">
                                    <div className="p-3 bg-red-500/30 rounded-xl animate-bounce">
                                        <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10 text-red-300" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex flex-col mb-3">
                                        <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                                            ⚠️ Your Websites Will Go Offline Soon!
                                        </h3>
                                        <span className="px-4 py-2 bg-red-500 text-white rounded-full text-sm font-bold border-2 border-red-300 animate-pulse self-start">
                                            ACTION REQUIRED
                                        </span>
                                    </div>
                                    <p className="text-gray-300 text-base sm:text-lg mb-4">
                                        Your Web Server expire on{' '}
                                        <span className="font-bold text-white bg-gradient-to-r from-red-500 to-orange-500 px-2 py-1 sm:px-3 sm:py-1 rounded-lg">
                                            {expiryDate}
                                        </span>
                                    </p>

                                    {/* Domain Status Grid */}
                                    <div className="grid grid-cols-1 gap-4">
                                        {domains.map((domain, index) => (
                                            <div key={index} className="bg-white/5 p-4 sm:p-5 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all duration-300">
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="p-2 bg-blue-500/20 rounded-lg">
                                                            <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-white text-base sm:text-lg">{domain.name}</div>
                                                            {/* <div className="text-xs sm:text-sm text-gray-400">{domain.type}</div> */}
                                                        </div>
                                                    </div>
                                                    {/* <div className="flex items-center justify-between sm:justify-end gap-3">
                            <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-medium">
                              {domain.status}
                            </span>
                            <div className="text-xs text-gray-400">{domain.visitors}</div>
                          </div> */}
                                                </div>
                                                <div className=" mt-3 pt-3 border-t border-white/10">
                                                    {/* <div className="flex items-center text-sm">
                                                        <IndianRupee className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 mr-1" />
                                                        <span className="text-gray-400">Domain Renewal:</span>
                                                        <span className="ml-2 font-bold text-white">{domain.renewal}</span>
                                                    </div> */}
                                                    {/* <div className="flex items-center text-sm">
                                                        <IndianRupee className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 mr-1" />
                                                        <span className="text-gray-400">Web Hosting Renewal:</span>
                                                        <span className="ml-2 font-bold text-white">{domain.webrenewal}</span>
                                                    </div> */}
                                                    <div className="flex items-center text-sm">
                                                        <IndianRupee className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 mr-1" />
                                                        <span className="text-gray-400">Server Renewal:</span>
                                                        <span className="ml-2 font-bold text-white">{domain.serverrenewal}</span>
                                                    </div>
                                                    <div className="flex items-center text-sm">
                                                        <IndianRupee className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 mr-1" />
                                                        <span className="text-gray-400">Total Renewal Price:</span>
                                                        <span className="ml-2 font-bold text-white">{domain.total}</span>
                                                    </div>
                                                    {/* <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 animate-pulse" /> */}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* Footer */}
                        <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/10">
                            <p className="text-center text-gray-500 text-xs sm:text-sm">
                                By proceeding, you agree to Hostinger's Terms of Service •
                                <span className="text-yellow-400 ml-2">⚠️ Expires in: {timeLeft}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add inline styles for animations */}
            <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulseBorder {
          0%, 100% { border-color: rgba(239, 68, 68, 0.4); }
          50% { border-color: rgba(245, 158, 11, 0.6); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
        
        .animate-pulseBorder {
          animation: pulseBorder 2s infinite;
        }
      `}</style>
        </>
    );
};

export default HostingerExpiryModal;
